import {Flag, Parameter, Parameters, Rank, Tooltip} from 'platform/components';
import {Box, HStack, Icon, Integer, Show, Text, VStack} from 'platform/foundation';
import {NumberFormatter} from 'platform/locale';

import {ReactElement} from 'react';

import {isNil, isNotNil} from 'ramda';
import {floor, isTrue} from 'ramda-adjunct';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {buildArray, EMPTY_PLACEHOLDER, isNotNilOrEmpty} from 'shared/web';

import {AuctionCountdownCell} from '../components/AuctionPreview/components/AuctionCountdownCell';
import {Clamp} from '../components/Clamp/Clamp';
import {HitlistBlacklistFlag} from '../components/HitlistBlacklistFlag/HitlistBlacklistFlag';
import {HeaderCellTooltip} from '../components/SourcingTable/components/HeaderCellTooltip';
import {Column} from '../components/SourcingTable/utils/getSourcingTableColumns';
import {AuctionEnumLookup} from '../hooks/useAuctionEnumLookup';
import {CarStatusFilter} from '../pages/AllOffers/types/CarStatusFilter';
import {AuctionSourcingListItem} from './adaptAuctionVehicleToSourcingItem';
import {buildAuctionVehicleTitle} from './buildAuctionVehicleTitle';
import {decimalToPercentRank} from './decimalToPercentRank';
import {getAuctionDisplayPrice} from './getAuctionDisplayPrice';
import {getAuctionTypePresentation} from './getAuctionTypePresentation';
import {getSuspiciousReasonsLabel} from './getSuspiciousReasonsLabel';
import {isHiddenAuctionType} from './isHiddenAuctionType';
import {resolveAuctionEnumLabel} from './resolveAuctionEnumLabel';

/**
 * Cell props for the auction table — mirrors the CarsCell shape but with
 * AuctionSourcingListItem as the row data type.
 */
const MINIMUM_PRICE_PREFIX = '≥ ';

type AuctionCell = {
  row: {
    original: AuctionSourcingListItem;
  };
};

/**
 * Type guard that narrows a CarsCell to AuctionCell.
 *
 * Auction rows are passed through the adaptAuctionVehicleToSourcingItem adapter in AllOffersPage.
 * `auctionVehicle.auctionId` is unique to auction rows and absent from SourcingVehicleListItemResponseBody,
 * so its presence is a reliable discriminant.
 */
function isAuctionCell(props: {row: {original: object}}): props is AuctionCell {
  return 'auctionVehicle' in props.row.original;
}

const cell =
  (render: (props: AuctionCell) => ReactElement): Column['Cell'] =>
  (props) => {
    if (isAuctionCell(props)) {
      return render(props);
    }

    return <></>;
  };

export type GetAuctionTableColumnsParams = {
  formatNumber: NumberFormatter;
  enumLookup: AuctionEnumLookup;
};

export function getAuctionTableColumns(params: GetAuctionTableColumnsParams): Column[] {
  return buildArray<Column>()
    .add({
      id: 'MAKE_MODEL_VARIANT_SPEC',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipMakeModelVariant')}>
          <Text size="xxSmall" color="secondary" noWrap>
            {i18n.t('entity.vehicle.labels.makeModelVariantSpec')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const vehicle = row.original.auctionVehicle;
        const sourcingVehicle = row.original.sourcingVehicle;
        const suspiciousReasons = getSuspiciousReasonsLabel(sourcingVehicle?.suspiciousReason);

        const resolve = (value: string | null | undefined) =>
          resolveAuctionEnumLabel(value, params.enumLookup);

        const firstLine = buildAuctionVehicleTitle(
          vehicle.make,
          vehicle.model,
          vehicle.adTitle,
          params.enumLookup
        );

        const auctionType = getAuctionTypePresentation(vehicle.auctionType);
        const secondLineParams = buildArray<Parameter>()
          .when(isNotNil(auctionType), {icon: auctionType?.icon, tooltip: auctionType?.label})
          .when(isNotNilOrEmpty(vehicle.year), String(vehicle.year))
          .when((vehicle.power ?? 0) > 0, `${vehicle.power} kW`)
          .when(isNotNilOrEmpty(vehicle.transmission), resolve(vehicle.transmission) ?? '')
          .when(isNotNilOrEmpty(vehicle.driveType), resolve(vehicle.driveType) ?? '')
          .when(isNotNilOrEmpty(vehicle.fuelType), resolve(vehicle.fuelType) ?? '')
          .when(isNotNilOrEmpty(vehicle.carStyle), resolve(vehicle.carStyle) ?? '')
          .when(
            isNotNilOrEmpty(vehicle.mileage),
            `${params.formatNumber(vehicle.mileage ?? 0, 0)} km`
          );

        return (
          <VStack spacing={1}>
            <HStack spacing={1} align="center">
              <Show when={!!row.original.isNew}>
                <Tooltip label={i18n.t('entity.vehicle.labels.unseen')}>
                  <Box
                    backgroundColor="palettes.green.60.100"
                    width={3}
                    height={3}
                    borderRadius="circular"
                  />
                </Tooltip>
              </Show>
              <Show when={isTrue(sourcingVehicle?.isSold)}>
                <Flag label={i18n.t('entity.vehicle.labels.sold')} size="small" />
              </Show>
              <Tooltip label={firstLine}>
                <Clamp lines={1}>
                  <Text size="small" alternative>
                    {firstLine}
                  </Text>
                </Clamp>
              </Tooltip>
              <Show when={isNotNilOrEmpty(row.original.vehicleSummary?.comment)}>
                <Tooltip label={row.original.vehicleSummary?.comment}>
                  <Icon value="communication/comment" size={5} />
                </Tooltip>
              </Show>
              <Show whenFeatureEnabled={featureFlags.SOURCING_HITLIST_BLACKLIST}>
                <HitlistBlacklistFlag sourcingVehicle={sourcingVehicle} />
              </Show>
              <Show
                whenFeatureEnabled={featureFlags.SOURCING_SUSPICIOUS_VEHICLES}
                when={sourcingVehicle.suspicious}
              >
                <Tooltip label={suspiciousReasons}>
                  <Icon value="alert/warning" color="severity.warning" size={5} />
                </Tooltip>
              </Show>
            </HStack>
            <Parameters
              size="xSmall"
              color="secondary"
              parameters={secondLineParams}
              data-testid={testIds.sourcing.auctions('vehicleInfo-parameters')}
            />
          </VStack>
        );
      }),
    })
    .add({
      id: 'CURRENT_BID',
      Header: (
        <HeaderCellTooltip label={i18n.t('general.labels.price')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('general.labels.price')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const originalPrice = isHiddenAuctionType(row.original.auctionVehicle.auctionType)
          ? row.original.auctionVehicle.originalPriceMinimum
          : row.original.auctionVehicle.originalPriceActual;
        const currentBid = getAuctionDisplayPrice(row.original.auctionVehicle);
        const auctionType = row.original.auctionVehicle.auctionType?.toLowerCase();
        const isHiddenType = isHiddenAuctionType(auctionType);
        const buyNowPrice = row.original.auctionVehicle.priceBuyNow;
        const hasSealedBuyNow = isHiddenType && (buyNowPrice ?? 0) > 0;
        const isCurrentBidAvailable = isNotNil(currentBid) && currentBid > 0;
        const originalPriceLabel = isHiddenType
          ? i18n
              .t('entity.auction.labels.minBidHint', {
                amount: params.formatNumber(originalPrice ?? currentBid ?? 0, 0),
              })
              .trim()
          : params.formatNumber(originalPrice ?? 0, 0);

        return (
          <VStack spacing={1} align="flex-end">
            <Show when={isCurrentBidAvailable}>
              <Tooltip label={originalPriceLabel}>
                <Text size="xSmall" alternative align="right">
                  {isHiddenType ? MINIMUM_PRICE_PREFIX : ''}
                  {params.formatNumber(currentBid ?? 0, 0)}
                </Text>
              </Tooltip>
            </Show>
            <Show when={isHiddenType && !isCurrentBidAvailable && !hasSealedBuyNow}>
              <Text size="xSmall" alternative color="secondary" align="right">
                {i18n.t('entity.auction.labels.priceNotDisclosed')}
              </Text>
            </Show>
            <Show when={hasSealedBuyNow}>
              <Tooltip
                label={`${i18n.t('entity.auction.labels.buyNow')}: ${params.formatNumber(row.original.auctionVehicle.originalPriceBuyNow ?? buyNowPrice ?? 0, 0)}`}
              >
                <HStack align="center" spacing={1}>
                  <Icon value="action/shopping_cart" size={3} color="text.secondary" />
                  <Text size="xSmall" color="secondary" align="right">
                    {params.formatNumber(buyNowPrice ?? 0, 0)}
                  </Text>
                </HStack>
              </Tooltip>
            </Show>
            <Show when={!isHiddenType && !isCurrentBidAvailable}>
              <Text size="xSmall" alternative color="secondary" align="right">
                {EMPTY_PLACEHOLDER}
              </Text>
            </Show>
          </VStack>
        );
      }),
      width: 18 as Integer,
    })
    .add({
      id: 'MARGIN',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.margin')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('entity.vehicle.labels.margin')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const marginValue = row.original.auctionVehicle.maximalPossibleMargin;
        const isHiddenType = isHiddenAuctionType(row.original.auctionVehicle.auctionType);
        const vehicleCosts = row.original.summary?.vehicleCosts;
        const marginWithoutCost = params.formatNumber((marginValue ?? 0) - (vehicleCosts ?? 0), 0);
        const formattedMargin =
          !isHiddenType && isNotNil(marginValue)
            ? params.formatNumber(marginValue, 0)
            : EMPTY_PLACEHOLDER;

        const marginText = (
          <Text
            size="xSmall"
            align="right"
            color={!isHiddenType && isNotNil(marginValue) && marginValue < 0 ? 'danger' : undefined}
          >
            {formattedMargin}
          </Text>
        );

        if (isHiddenType || isNil(marginValue)) {
          return marginText;
        }

        return (
          <Tooltip
            label={i18n.t('entity.vehicle.labels.marginWithoutTotalCosts', {
              marginWithoutCost,
            })}
          >
            {marginText}
          </Tooltip>
        );
      }),
      width: 16 as Integer,
    })
    .add({
      id: 'RECOMMEND',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipRecommendedPrice')}>
          <Text size="xxSmall" color="secondary" align="right" noWrap>
            {i18n.t('entity.vehicle.labels.recommendedPrice')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const predictedPrice = row.original.auctionVehicle.predictedPrice;
        const formattedPredictedPrice = isNotNil(predictedPrice)
          ? params.formatNumber(predictedPrice, 0)
          : EMPTY_PLACEHOLDER;

        return (
          <Text size="xSmall" align="right">
            {formattedPredictedPrice}
          </Text>
        );
      }),
      width: 16 as Integer,
    })
    .add({
      id: 'POSITION',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipPosition')}>
          <Text size="xxSmall" color="secondary" align="center" noWrap>
            {i18n.t('entity.vehicle.labels.position')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => {
        const marketPosition = row.original.auctionVehicle.marketPosition;
        const marketPositionCount = row.original.auctionVehicle.marketPositionCount;

        const positionText =
          isNotNil(marketPosition) && isNotNil(marketPositionCount)
            ? `${marketPosition}/${marketPositionCount}`
            : EMPTY_PLACEHOLDER;

        const positionPercentage =
          isNotNil(marketPosition) && isNotNil(marketPositionCount) && marketPositionCount > 0
            ? Math.round(((marketPositionCount - marketPosition) / marketPositionCount) * 100)
            : null;

        const percentText = isNotNil(positionPercentage) ? `${positionPercentage}%` : undefined;

        return (
          <VStack align="center">
            <Text size="xSmall" noWrap>
              {positionText}
            </Text>
            <Show when={isNotNil(percentText)}>
              <Text size="xSmall" color="secondary">
                {percentText}
              </Text>
            </Show>
          </VStack>
        );
      }),
      width: 12 as Integer,
    })
    .add({
      id: 'LID',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipLID')}>
          <Text size="xxSmall" color="secondary" noWrap>
            {i18n.t('entity.vehicle.labels.LID')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => (
        <Text size="xSmall">
          {isNotNil(row.original.auctionVehicle.turnover)
            ? params.formatNumber(row.original.auctionVehicle.turnover, 0)
            : EMPTY_PLACEHOLDER}
        </Text>
      )),
      width: 8 as Integer,
    })
    .add({
      id: 'FEATURES',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.vehicle.labels.tooltipRank')}>
          <Text size="xxSmall" color="secondary" noWrap>
            {i18n.t('entity.vehicle.labels.features')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => (
        <Tooltip
          label={decimalToPercentRank(row.original.auctionVehicle.featureScore ?? undefined)}
        >
          <Rank
            percentage={floor((row.original.auctionVehicle.featureScore ?? 0) * 100)}
            data-testid={testIds.sourcing.auctions('rank-tooltip')}
          />
        </Tooltip>
      )),
      width: 10 as Integer,
    })
    .add({
      id: 'AUCTION_END',
      Header: (
        <HeaderCellTooltip label={i18n.t('entity.auction.labels.auctionEnds')}>
          <Text size="xxSmall" color="secondary" noWrap>
            {i18n.t('entity.auction.labels.auctionEnd')}
          </Text>
        </HeaderCellTooltip>
      ),
      Cell: cell(({row}) => (
        <AuctionCountdownCell
          endTime={row.original.auctionVehicle.carEndTime}
          isEnded={row.original.auctionVehicle.carStatus === CarStatusFilter.ENDED}
        />
      )),
      width: 24 as Integer,
    });
}
